import type { FileMetaDto } from "../api";
import { DocumentsTypes } from "../types";

export type FileMetaWithProgress = FileMetaDto & { progress?: number };

export const normalizeBaseName = (s: string) => String(s || "").replace(/\.ifc$/i, "");

export const createTempFileMeta = (params: {
  id: string;
  baseName: string;
  sourceId: string;
  size: number;
  type: (typeof DocumentsTypes)[keyof typeof DocumentsTypes];
  progress: number;
  ext: string;
  createdAt?: number;
}): FileMetaWithProgress => {
  const createdAtSeconds = Number.isFinite(params.createdAt) ? (params.createdAt as number) : Math.floor(Date.now() / 1000);
  return {
    id: params.id,
    baseName: params.baseName,
    sourceId: params.sourceId,
    size: params.size,
    type: params.type,
    ext: params.ext,
    progress: params.progress,
    version: 1,
    createdAt: createdAtSeconds,
    modifiedAt: createdAtSeconds,
    author: "",
    parentId: null,
    isDeleted: false,
    sourceType: "PROJECT_ID" as unknown as FileMetaDto["sourceType"],
  };
};

export const pendingUploadToFileMeta = (
  projectId: string,
  pending: {
    rawName: string;
    baseName: string;
    size: number;
    percent: number;
    status: "progress" | "success" | "error";
    updatedAt: number;
  },
): FileMetaWithProgress => {
  const ext = pending.rawName.split(".").pop() || "ifc";
  const isPdf = ext === "pdf";
  return createTempFileMeta({
    id: pending.rawName,
    baseName: pending.baseName,
    sourceId: projectId,
    size: pending.size,
    type: isPdf ? DocumentsTypes.pdf_document : DocumentsTypes.model,
    progress: pending.percent,
    ext,
    createdAt: Math.floor(pending.updatedAt / 1000),
  });
};

export const mergeServerFilesWithUploadProgress = (serverFiles: FileMetaDto[], progressRows: FileMetaWithProgress[]): FileMetaDto[] => {
  const byBaseName = new Map<string, FileMetaWithProgress>();
  progressRows.forEach((row) => {
    byBaseName.set(normalizeBaseName(row.baseName), row);
  });

  return serverFiles.map((file) => {
    const key = normalizeBaseName(file.baseName);
    const progress = byBaseName.get(key);
    if (progress) {
      return { ...file, progress: progress.progress } as FileMetaWithProgress;
    }
    return file;
  });
};

export const collectUploadProgressRows = (
  projectId: string,
  _activeTab: string,
  _currentFiles: FileMetaDto[],
  pending: Record<
    string,
    { rawName: string; baseName: string; size: number; percent: number; status: "progress" | "success" | "error"; updatedAt: number }
  >,
): FileMetaWithProgress[] => {
  return Object.values(pending)
    .filter((p) => p.status === "progress")
    .map((p) => pendingUploadToFileMeta(projectId, p));
};

export const applyUploadProgressToFiles = (
  files: FileMetaDto[],
  _rawName: string,
  normalizedFileName: string,
  percent: number,
  createTempFileMetaFn: () => FileMetaWithProgress,
): FileMetaDto[] => {
  const idx = files.findIndex((f) => normalizeBaseName(f.baseName) === normalizedFileName);
  if (idx >= 0) {
    const next = [...files];
    next[idx] = { ...next[idx], progress: percent } as FileMetaWithProgress;
    return next;
  }
  return [...files, createTempFileMetaFn()];
};

export const removeUploadProgressForFile = (files: FileMetaDto[], normalizedFileName: string, _allFiles: FileMetaDto[]): FileMetaDto[] => {
  return files.filter((f) => {
    const withProgress = f as FileMetaWithProgress;
    if (typeof withProgress.progress !== "number") return true;
    return normalizeBaseName(f.baseName) !== normalizedFileName;
  });
};
